import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useImpersonationNavigate } from '../hooks/useImpersonationNavigate';
import { useAuthStore } from '../stores/authStore';
import { Eye, EyeOff, Lock, Mail, Loader } from 'lucide-react';
import TwoFactorForm from './login/TwoFactorForm';

function Login() {
    const navigate = useImpersonationNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { login, isLoading, error } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState('');

    // 2FA State from URL - survives remounts!
    const requires2FA = searchParams.get('step') === '2fa';
    const twoFactorMethod = searchParams.get('method') || 'totp';
    const twoFactorMessage = requires2FA
        ? (twoFactorMethod === 'totp' ? 'Enter code from your authenticator app' : 'Check your email for the code')
        : '';

    // Credential persistence - email from URL, password from sessionStorage
    const savedEmail = searchParams.get('email') || '';
    const [email, setEmail] = useState(savedEmail);
    const [password, setPassword] = useState('');

    // Restore password from sessionStorage on mount
    useEffect(() => {
        if (requires2FA) {
            const storedPassword = sessionStorage.getItem('login_password');
            if (storedPassword) {
                setPassword(storedPassword);
            }
            // Also restore email from URL
            const urlEmail = searchParams.get('email');
            if (urlEmail) {
                setEmail(urlEmail);
            }
        }
    }, [requires2FA, searchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const result = await login(email, password, requires2FA ? twoFactorCode : null);

        if (result === true) {
            // Clean up stored credentials
            sessionStorage.removeItem('login_password');
            navigate('/');
        } else if (result?.requires2FA) {
            // Store password in sessionStorage (more secure than URL)
            sessionStorage.setItem('login_password', password);
            // Store email in URL (safe to expose)
            setSearchParams({ step: '2fa', method: result.method, email: email });
        }
    };

    const handleBack = () => {
        setTwoFactorCode('');
        sessionStorage.removeItem('login_password');
        setSearchParams({}); // Clear URL params
    };

    return (
        <div className="login-page" style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-primary)',
            padding: 'var(--spacing-4)'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '420px'
            }}>
                {/* Logo */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: 'var(--spacing-8)'
                }}>
                    <img 
                        src="/Favicon.png" 
                        alt="Always In Logo"
                        style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'contain',
                            margin: '0 auto var(--spacing-4)'
                        }}
                    />
                    <h1 style={{
                        fontSize: 'var(--font-size-2xl)',
                        fontWeight: 700,
                        background: 'var(--accent-gradient)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>
                        Always In Platform
                    </h1>
                    <p style={{
                        color: 'var(--text-secondary)',
                        marginTop: 'var(--spacing-2)'
                    }}>
                        Business Management Dashboard
                    </p>
                </div>

                {/* Login Card */}
                <div className="card" style={{ padding: 'var(--spacing-6)' }}>
                    {requires2FA ? (
                        <TwoFactorForm
                            twoFactorCode={twoFactorCode}
                            setTwoFactorCode={setTwoFactorCode}
                            twoFactorMethod={twoFactorMethod}
                            twoFactorMessage={twoFactorMessage}
                            error={error}
                            isLoading={isLoading}
                            onSubmit={handleSubmit}
                            onBack={handleBack}
                        />
                    ) : (
                        /* Regular Login Screen */
                        <>
                            <h2 style={{
                                fontSize: 'var(--font-size-xl)',
                                fontWeight: 600,
                                marginBottom: 'var(--spacing-6)',
                                textAlign: 'center'
                            }}>
                                Sign In
                            </h2>

                            {error && (
                                <div style={{
                                    padding: 'var(--spacing-3)',
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    border: '1px solid var(--error)',
                                    borderRadius: 'var(--radius-md)',
                                    color: 'var(--error)',
                                    fontSize: 'var(--font-size-sm)',
                                    marginBottom: 'var(--spacing-4)'
                                }}>
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label">Email</label>
                                    <div style={{ position: 'relative' }}>
                                        <Mail size={18} style={{
                                            position: 'absolute',
                                            left: 'var(--spacing-3)',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: 'var(--text-tertiary)'
                                        }} />
                                        <input
                                            type="email"
                                            className="form-input"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            style={{ paddingLeft: 'calc(var(--spacing-3) + 24px)' }}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Password</label>
                                    <div style={{ position: 'relative' }}>
                                        <Lock size={18} style={{
                                            position: 'absolute',
                                            left: 'var(--spacing-3)',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: 'var(--text-tertiary)'
                                        }} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className="form-input"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            style={{
                                                paddingLeft: 'calc(var(--spacing-3) + 24px)',
                                                paddingRight: 'calc(var(--spacing-3) + 24px)'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{
                                                position: 'absolute',
                                                right: 'var(--spacing-3)',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--text-tertiary)',
                                                cursor: 'pointer',
                                                padding: 0
                                            }}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    <div style={{ textAlign: 'right', marginTop: 'var(--spacing-2)' }}>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/forgot-password')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--accent-purple)',
                                                fontSize: 'var(--font-size-xs)',
                                                cursor: 'pointer',
                                                padding: 0
                                            }}
                                        >
                                            Forgot your password?
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isLoading}
                                    style={{
                                        width: '100%',
                                        padding: 'var(--spacing-3)',
                                        marginTop: 'var(--spacing-4)'
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader size={18} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                                            Signing in...
                                        </>
                                    ) : (
                                        'Sign In'
                                    )}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                <p style={{
                    textAlign: 'center',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-tertiary)',
                    marginTop: 'var(--spacing-6)'
                }}>
                    © 2026 Always In LTD. All rights reserved.
                </p>
            </div>

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
}

export default Login;
